import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@ais-ui/utils';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  FilterCriteria,
  filterOptionsTemplate,
  HumanOperator,
  humanOperatorToOperator,
  operatorAndValueToHumanOperator,
} from '../constants';
import { PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { v4 as uuidv4 } from 'uuid';
import { TokenInfo } from './utils/utils';
import { ShapeSelect } from '../../Shapes/ShapeSelects/ShapeSelect';
import { checkIsBooleanField, getValueTypesByField } from '../utils';
import { useFilterContext } from '../../Map/FilterContext';
import { useLocalization } from '@ais-ui/locale-management';
import { FilterTokenizer } from './utils/FilterTokenizer';
import { TokenizerResult } from './utils/types';

export interface FilterBuilderProps {
  id?: string;
  value: string;
  onChange: (value: string | undefined) => void;
}

export type SelectType = Record<'value' | 'label', string>;

interface FieldSelectProps {
  selectedField: string | undefined;
  setSelectedField: (value: string | undefined) => void;
}

interface OperatorSelectProps {
  selectedField: string | undefined;
  selectedHumanOperator: HumanOperator | undefined;
  setSelectedHumanOperator: (value: HumanOperator | undefined) => void;
}

interface ValueSelectProps {
  selectedField: string | undefined;
  selectedValue: string | undefined;
  setSelectedValue: (value: string | undefined) => void;
}

interface BuildItemRecord {
  id: string;
  selectedField: string | undefined;
  selectedLogicalOperator: string | undefined;
  selectedHumanOperator: HumanOperator | undefined;
  selectedValue: string | undefined | boolean;
}

function mapTokenizerResultToBuildItems(
  tokenizerResult: TokenizerResult,
  filterOptions: Record<string, FilterCriteria>
): BuildItemRecord[] {
  const { tokens } = tokenizerResult;

  if (tokens.length === 0) {
    return [
      {
        id: uuidv4(),
        selectedLogicalOperator: undefined,
        selectedField: undefined,
        selectedHumanOperator: undefined,
        selectedValue: undefined,
      },
    ];
  }

  const expressions: BuildItemRecord[] = [];
  let currentExpression: Partial<BuildItemRecord> = { id: uuidv4() };
  let expectingLogical = false;

  tokens.forEach((token, index) => {
    switch (token.type) {
      case 'field':
        if (expectingLogical) {
          expressions.push(currentExpression as BuildItemRecord);
          currentExpression = { id: uuidv4(), selectedField: token.value };
          expectingLogical = false;
        } else {
          currentExpression.selectedField = token.value;
        }
        break;

      case 'operator':
        if (currentExpression.selectedField) {
          const humanOp = Object.entries(HumanOperator).find(([key, value]) => {
            return humanOperatorToOperator(value) === token.value;
          });
          if (humanOp) {
            currentExpression.selectedHumanOperator =
              humanOp[1] as HumanOperator;
          }
        }
        break;

      case 'value':
        currentExpression.selectedValue = token.value;
        expectingLogical = true;
        break;

      case 'logical':
        if (currentExpression.selectedField) {
          expressions.push(currentExpression as BuildItemRecord);
          currentExpression = {
            id: uuidv4(),
            selectedLogicalOperator: token.value.toLowerCase(),
          };
          expectingLogical = false;
        }
        break;
    }
  });

  if (currentExpression.selectedField) {
    expressions.push(currentExpression as BuildItemRecord);
  }

  return expressions.map((expr, index) => {
    const field = expr.selectedField;

    if (index > 0 && expressions[index - 1]?.selectedLogicalOperator) {
      expr.selectedLogicalOperator =
        expressions[index - 1].selectedLogicalOperator;
    } else if (index === 0) {
      expr.selectedLogicalOperator = undefined;
    }

    if (
      field &&
      checkIsBooleanField(getValueTypesByField(field, filterOptions))
    ) {
      return {
        ...expr,
        selectedHumanOperator: HumanOperator.Boolean,
        selectedValue: true,
      } as BuildItemRecord;
    }

    if (expr.selectedValue && typeof expr.selectedValue === 'string') {
      const value = expr.selectedValue;
      const operator = expr.selectedHumanOperator;

      if (
        operator === HumanOperator.Equal ||
        operator === HumanOperator.NotEqual
      ) {
        if (value.startsWith('*') && value.endsWith('*')) {
          expr.selectedHumanOperator =
            operator === HumanOperator.Equal
              ? HumanOperator.Contains
              : HumanOperator.NotContains;
          expr.selectedValue = value.slice(1, -1);
        } else if (value.startsWith('*')) {
          expr.selectedHumanOperator =
            operator === HumanOperator.Equal
              ? HumanOperator.EndsWith
              : HumanOperator.DoesNotEndWith;
          expr.selectedValue = value.slice(1);
        } else if (value.endsWith('*')) {
          expr.selectedHumanOperator =
            operator === HumanOperator.Equal
              ? HumanOperator.StartsWith
              : HumanOperator.DoesNotStartWith;
          expr.selectedValue = value.slice(0, -1);
        }
      }
    }

    return expr as BuildItemRecord;
  });
}

const FieldSelect = ({ selectedField, setSelectedField }: FieldSelectProps) => {
  const { messages } = useLocalization();
  const { filterOptions } = useFilterContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const availableFieldSelectables: SelectType[] = useMemo(() => {
    return Object.keys(filterOptions)
      .filter((filterName) =>
        filterName.toLowerCase().includes(search.toLowerCase())
      )
      .map((filterName) => ({
        label: filterName.toLowerCase(),
        value: filterName.toLowerCase(),
      }));
  }, [filterOptions, search]);

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const COMMAND_INPUT_HEIGHT = 40;
    const COMMAND_ITEM_HEIGHT = 32;
    const COMMAND_EMPTY_HEIGHT = 68;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 300;
    const MIN_HEIGHT = 60;

    let contentHeight = COMMAND_INPUT_HEIGHT + PADDING_AND_BORDERS;

    if (!availableFieldSelectables.length) {
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else {
      contentHeight += availableFieldSelectables.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(Math.max(contentHeight, MIN_HEIGHT), MAX_HEIGHT);
  }, [availableFieldSelectables.length]);

  return (
    <Popover
      open={open}
      onOpenChange={() => {
        setOpen(!open);
      }}
      modal={true}
    >
      <PopoverTrigger asChild>
        <Button
          id="filter-field"
          variant="outline"
          role="combobox"
          onClick={() => setOpen(!open)}
          className="w-full min-w-[120px] max-w-[200px] justify-between pr-3"
        >
          <div className="flex items-center justify-between w-full">
            <span className="truncate mr-2">
              {selectedField ? (
                availableFieldSelectables?.find(
                  (field) => field.value === selectedField
                )?.label
              ) : (
                <span className="text-muted-foreground">Select a field</span>
              )}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-1" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-full p-0"
        style={{ height: `${calculatedHeight}px` }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={messages.filtersBuilderFilterFieldPlaceholder}
            onValueChange={(value) => {
              setSearch(value);
            }}
            value={search}
          />
          <CommandEmpty>No fields matches search</CommandEmpty>
          <ScrollArea className="flex-grow p-0 overflow-y-auto border rounded-lg">
            {availableFieldSelectables.map((fieldSelect) => (
              <CommandItem
                key={fieldSelect.value}
                value={fieldSelect.value}
                onSelect={(currentValue) => {
                  setSelectedField(
                    currentValue === selectedField ? undefined : currentValue
                  );
                  setOpen(false);
                }}
              >
                <div className="flex justify-between w-full">
                  {fieldSelect.label}
                  <Check
                    className={cn(
                      'mr-1 h-4 w-4',
                      selectedField === fieldSelect.value
                        ? 'opacity-100'
                        : 'opacity-0'
                    )}
                  />
                </div>
              </CommandItem>
            ))}
          </ScrollArea>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

function getHumanOperatorsByField(
  field: string | undefined,
  filterOptions: Record<string, FilterCriteria>
): HumanOperator[] {
  if (!field) {
    return [];
  }

  const uniqueHumanOperators = new Set<HumanOperator>();
  const uppercasedSelectedField = field.toUpperCase();

  if (
    Object.prototype.hasOwnProperty.call(filterOptions, uppercasedSelectedField)
  ) {
    filterOptions[uppercasedSelectedField].operators.forEach(
      (humanOperator) => {
        uniqueHumanOperators.add(humanOperator);
      }
    );
  }

  return Array.from(new Set(uniqueHumanOperators));
}

const OperatorSelect = ({
  selectedField,
  selectedHumanOperator,
  setSelectedHumanOperator,
}: OperatorSelectProps) => {
  const [open, setOpen] = useState(false);
  const { filterOptions } = useFilterContext();
  const { messages } = useLocalization();

  const availableOperatorSelectables = useMemo(() => {
    const humanOperators = getHumanOperatorsByField(
      selectedField,
      filterOptions
    );
    return humanOperators;
  }, [filterOptions, selectedField]);

  // Calculate dynamic height for operators
  const calculatedOperatorHeight = useMemo(() => {
    const COMMAND_ITEM_HEIGHT = 30;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 200;
    const MIN_HEIGHT = 60;

    let contentHeight = PADDING_AND_BORDERS;

    if (!availableOperatorSelectables.length) {
      contentHeight += 50; // Small height for no items
    } else {
      contentHeight +=
        availableOperatorSelectables.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(Math.max(contentHeight, MIN_HEIGHT), MAX_HEIGHT);
  }, [availableOperatorSelectables.length]);

  const isBooleanField = useMemo(() => {
    return checkIsBooleanField(
      getValueTypesByField(selectedField, filterOptions)
    );
  }, [filterOptions, selectedField]);

  if (isBooleanField) {
    return (
      <div className="flex w-full h-full items-center text-xs text-muted-foreground">
        <p>{messages.FiltersBooleanFieldHelpText}</p>
      </div>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={() => {
        setOpen(!open);
      }}
      modal={true}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              id="filter-operator"
              variant="outline"
              role="combobox"
              disabled={isBooleanField}
              onClick={() => setOpen(!open)}
              className={cn(
                'w-full justify-between pr-3',
                !selectedField &&
                  'opacity-50 cursor-default hover:bg-background',
                isBooleanField && 'invisible'
              )}
            >
              {selectedHumanOperator ? (
                availableOperatorSelectables?.find(
                  (field) => field === selectedHumanOperator
                )
              ) : (
                <span>{messages.filtersBuilderFilterOperatorPlaceholder}</span>
              )}
              <span className="flex-1"></span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {messages.filtersBuilderFilterOperatorTooltipContent}
        </TooltipContent>
      </Tooltip>
      {selectedField && (
        <PopoverContent
          align="start"
          className="w-full p-0"
          style={{ height: `${calculatedOperatorHeight}px` }}
        >
          <Command shouldFilter={false}>
            <ScrollArea className="flex-grow p-0 overflow-y-auto border rounded-lg">
              {availableOperatorSelectables.map((humanOperator) => (
                <CommandItem
                  key={humanOperator.toString()}
                  value={humanOperator.toString()}
                  onSelect={(currentValue) => {
                    setSelectedHumanOperator(
                      currentValue === selectedHumanOperator?.toString()
                        ? undefined
                        : humanOperator
                    );
                    setOpen(false);
                  }}
                >
                  <div className="flex justify-between w-full">
                    {humanOperator.toString()}
                    <Check
                      className={cn(
                        'ml-4 mr-1 h-4 w-4',
                        selectedHumanOperator === humanOperator
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </ScrollArea>
          </Command>
        </PopoverContent>
      )}
    </Popover>
  );
};

function getValueSuggestionsByField(
  field: string | undefined,
  filterOptions: Record<string, FilterCriteria>
): string[] {
  if (!field) {
    return [];
  }

  const valueSuggestions: string[] = [];
  const uppercasedField = field.toUpperCase();

  if (Object.prototype.hasOwnProperty.call(filterOptions, uppercasedField)) {
    filterOptions[uppercasedField].values.forEach((suggestionValue) => {
      valueSuggestions.push(suggestionValue);
    });
  }

  return valueSuggestions;
}

const ValueSelect = ({
  selectedField,
  selectedValue,
  setSelectedValue,
}: ValueSelectProps) => {
  const { messages } = useLocalization();
  const { filterOptions } = useFilterContext();
  const [open, setOpen] = useState(false);

  const isBooleanField = useMemo(() => {
    return checkIsBooleanField(
      getValueTypesByField(selectedField, filterOptions)
    );
  }, [filterOptions, selectedField]);

  const availableValueSelectables = useMemo(() => {
    const values = getValueSuggestionsByField(selectedField, filterOptions);
    return values;
  }, [filterOptions, selectedField]);

  const filteredSuggestions = useMemo(() => {
    return availableValueSelectables.filter((value) =>
      value
        .toLowerCase()
        .includes(selectedValue ? selectedValue.toLowerCase() : '')
    );
  }, [availableValueSelectables, selectedValue]);

  // Calculate dynamic height for value suggestions
  const calculatedValueHeight = useMemo(() => {
    const COMMAND_ITEM_HEIGHT = 30;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 200;
    const MIN_HEIGHT = 60;

    let contentHeight = PADDING_AND_BORDERS;

    if (!filteredSuggestions.length) {
      contentHeight += 50; // Small height for no items
    } else {
      contentHeight += filteredSuggestions.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(Math.max(contentHeight, MIN_HEIGHT), MAX_HEIGHT);
  }, [filteredSuggestions.length]);

  if (isBooleanField) {
    return null;
  }

  if (selectedField?.toLowerCase() === 'position') {
    return (
      <ShapeSelect
        value={selectedValue}
        onChange={setSelectedValue}
        allowedGeometryTypes={['Polygon']}
      />
    );
  }

  if (selectedField?.toLowerCase() === 'circle_position') {
    return (
      <ShapeSelect
        value={selectedValue}
        onChange={setSelectedValue}
        allowedGeometryTypes={['Circle']}
      />
    );
  }

  const dataTypes = selectedField
    ? filterOptionsTemplate[selectedField.toUpperCase()]?.data_types
    : undefined;
  const inputType = dataTypes?.includes('number') ? 'number' : 'text';

  return (
    <Command
      className={cn(
        'overflow-visible bg-transparent',
        isBooleanField && 'invisible'
      )}
      shouldFilter={false}
    >
      <div className="relative">
        <div className="group border border-input px-3 py-2 text-sm rounded-md">
          <div className="flex gap-1 flex-wrap">
            <input
              id="filter-value"
              type={inputType}
              disabled={isBooleanField}
              value={selectedValue || ''}
              onChange={(e) => {
                setSelectedValue(e.target.value);
              }}
              onFocus={() => {
                setOpen(true);
              }}
              onBlur={(e) => {
                // Capture the current target before the timeout
                const currentTarget = e.currentTarget;
                setTimeout(() => {
                  if (
                    currentTarget &&
                    !currentTarget.contains(document.activeElement)
                  ) {
                    setOpen(false);
                  }
                }, 150);
              }}
              placeholder={messages.filtersBuilderFilterValuePlaceholder}
              className={cn(
                'flex w-full rounded-md bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 ml-2'
              )}
              autoComplete="off"
            />
          </div>
        </div>

        {selectedField && filteredSuggestions.length > 0 && open && (
          <div className="relative">
            <div
              className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in"
              style={{ height: `${calculatedValueHeight}px` }}
            >
              <ScrollArea className="flex-grow p-0 overflow-y-auto border rounded-lg h-full">
                {filteredSuggestions.map((valueSelect) => (
                  <CommandItem
                    key={valueSelect}
                    value={valueSelect}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onSelect={(currentValue) => {
                      setSelectedValue(valueSelect);
                      setOpen(false);
                    }}
                  >
                    <div className="flex justify-between w-full">
                      {valueSelect}
                      <Check
                        className={cn(
                          'mr-1 h-4 w-4',
                          selectedValue === valueSelect
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                    </div>
                  </CommandItem>
                ))}
              </ScrollArea>
            </div>
          </div>
        )}
      </div>
    </Command>
  );
};

interface BuildItemProps {
  isFirst: boolean;
  item: BuildItemRecord;
  onLogicalOperatorChange: (id: string, value: string | undefined) => void;
  onFieldChange: (id: string, value: string | undefined) => void;
  onOperatorChange: (id: string, value: HumanOperator | undefined) => void;
  onValueChange: (id: string, value: string | undefined) => void;
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
}

const BuildItem = ({
  isFirst,
  item,
  onLogicalOperatorChange,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onAddItem,
  onRemoveItem,
}: BuildItemProps) => {
  return (
    <div>
      {isFirst ? null : (
        <Select
          value={item.selectedLogicalOperator}
          onValueChange={(value) => {
            onLogicalOperatorChange(item.id, value);
          }}
        >
          <SelectTrigger className="w-20 font-medium mt-2">
            {item.selectedLogicalOperator}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="and">and</SelectItem>
            <SelectItem value="or">or</SelectItem>
          </SelectContent>
        </Select>
      )}
      <div className={cn('flex space-x-2', !isFirst && 'mt-2')}>
        <FieldSelect
          selectedField={item.selectedField}
          setSelectedField={(value) => {
            onFieldChange(item.id, value);
          }}
        />
        <OperatorSelect
          selectedField={item.selectedField}
          selectedHumanOperator={item.selectedHumanOperator}
          setSelectedHumanOperator={(value) => {
            onOperatorChange(item.id, value);
          }}
        />
        <ValueSelect
          selectedField={item.selectedField}
          selectedValue={
            typeof item.selectedValue === 'boolean' ? '' : item.selectedValue
          }
          setSelectedValue={(value) => {
            onValueChange(item.id, value);
          }}
        />
        <Button
          type="button"
          size="icon"
          disabled={isFirst}
          variant="outline"
          className="h-9 w-9 flex-shrink-0 flex-grow-0"
          onClick={() => {
            onRemoveItem(item.id);
          }}
        >
          <PiMinusThin className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-9 w-9 flex-shrink-0 flex-grow-0"
          onClick={() => {
            onAddItem();
          }}
        >
          <PiPlusThin className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

function mapTokenInfoToBuildItems(
  tokenInfo: TokenInfo,
  filterOptions: Record<string, FilterCriteria>
): BuildItemRecord[] {
  const maxLength = Math.max(
    tokenInfo.fields.length,
    tokenInfo.logicalOperators.length,
    tokenInfo.arithmeticOperators.length,
    tokenInfo.values.length
  );

  if (maxLength === 0) {
    return [
      {
        id: uuidv4(),
        selectedLogicalOperator: undefined,
        selectedField: undefined,
        selectedHumanOperator: undefined,
        selectedValue: undefined,
      },
    ];
  }

  return Array.from({ length: maxLength }).map((_, index) => {
    const field = tokenInfo.fields[index] || undefined;

    const logicalOperator =
      index === 0
        ? undefined
        : tokenInfo.logicalOperators[index - 1] || undefined;

    if (checkIsBooleanField(getValueTypesByField(field, filterOptions))) {
      return {
        id: uuidv4(),
        selectedField: field,
        selectedLogicalOperator: logicalOperator,
        selectedHumanOperator: HumanOperator.Boolean,
        selectedValue: true,
      };
    }

    const arithmeticOperator =
      tokenInfo.arithmeticOperators[index] || undefined;
    const value = tokenInfo.values[index] || undefined;

    let humanOperator = undefined;
    if (arithmeticOperator && value) {
      humanOperator = operatorAndValueToHumanOperator(
        arithmeticOperator,
        value
      );
    }

    return {
      id: uuidv4(),
      selectedField: field,
      selectedLogicalOperator: logicalOperator,
      selectedHumanOperator: humanOperator,
      selectedValue:
        typeof value === 'string' ? value.replace(/["*]/g, '') : value,
    };
  });
}

export const FilterBuilder = ({ id, value, onChange }: FilterBuilderProps) => {
  const { filterOptions } = useFilterContext();

  // CRITICAL FIX: Track if changes are coming from internal updates
  const isInternalUpdate = useRef(false);
  const lastGeneratedValue = useRef(value);

  const [items, setItems] = useState<BuildItemRecord[]>(() => {
    const tokenizerResult = FilterTokenizer.tokenize(value);
    return mapTokenizerResultToBuildItems(tokenizerResult, filterOptions);
  });

  // CRITICAL FIX: Only update items when value changes from EXTERNAL source
  useEffect(() => {
    // Skip if this change was generated internally
    if (isInternalUpdate.current || value === lastGeneratedValue.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (value) {
      const tokenizerResult = FilterTokenizer.tokenize(value);
      const newItems = mapTokenizerResultToBuildItems(
        tokenizerResult,
        filterOptions
      );
      setItems(newItems);
    }
  }, [value, filterOptions]);

  const generateDefinition = useCallback(
    (updatedItems: BuildItemRecord[]) => {
      let definition = '';
      for (const item of updatedItems) {
        if (
          !item.selectedField ||
          !item.selectedHumanOperator ||
          (item.selectedValue === undefined &&
            !checkIsBooleanField(
              getValueTypesByField(item.selectedField, filterOptions)
            ))
        ) {
          continue;
        }

        const types = getValueTypesByField(item.selectedField, filterOptions);

        let value;
        if (checkIsBooleanField(types)) {
          value = '';
        } else {
          if (
            item.selectedHumanOperator === HumanOperator.Contains ||
            item.selectedHumanOperator === HumanOperator.NotContains
          ) {
            value = `"*${item.selectedValue}*"`;
          } else if (
            item.selectedHumanOperator === HumanOperator.StartsWith ||
            item.selectedHumanOperator === HumanOperator.DoesNotStartWith
          ) {
            value = `"${item.selectedValue}*"`;
          } else if (
            item.selectedHumanOperator === HumanOperator.EndsWith ||
            item.selectedHumanOperator === HumanOperator.DoesNotEndWith
          ) {
            value = `"*${item.selectedValue}"`;
          } else {
            value = `"${item.selectedValue}"`;
          }
        }

        const operatorText = humanOperatorToOperator(
          item.selectedHumanOperator
        );

        if (item.selectedLogicalOperator) {
          if (checkIsBooleanField(types)) {
            definition += `${item.selectedLogicalOperator} ${item.selectedField} `;
          } else {
            definition += `${item.selectedLogicalOperator} ${item.selectedField} ${operatorText} ${value} `;
          }
        } else {
          if (checkIsBooleanField(types)) {
            definition += `${item.selectedField} `;
          } else {
            definition += `${item.selectedField} ${operatorText} ${value} `;
          }
        }
      }

      const newDefinition = definition.trim();

      // CRITICAL FIX: Mark as internal update and track the generated value
      if (newDefinition !== lastGeneratedValue.current) {
        isInternalUpdate.current = true;
        lastGeneratedValue.current = newDefinition;
        onChange(newDefinition);
      }
    },
    [filterOptions, onChange]
  );

  const handleLogicOperatorChange = useCallback(
    (id: string, newValue: string | undefined) => {
      setItems((prevItems) => {
        const newItems = prevItems.map((item) =>
          item.id === id
            ? { ...item, selectedLogicalOperator: newValue }
            : item.selectedLogicalOperator !== undefined &&
              item.selectedLogicalOperator !== null
            ? { ...item, selectedLogicalOperator: newValue }
            : item
        );
        // Use microtask to ensure state update completes first
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition]
  );

  const handleFieldChange = useCallback(
    (id: string, newValue: string | undefined) => {
      const types = getValueTypesByField(newValue, filterOptions);
      if (checkIsBooleanField(types)) {
        setItems((prevItems) => {
          const newItems = prevItems.map((item) =>
            item.id === id
              ? {
                  ...item,
                  selectedField: newValue,
                  selectedHumanOperator: HumanOperator.Boolean,
                  selectedValue: true,
                }
              : item
          );
          queueMicrotask(() => generateDefinition(newItems));
          return newItems;
        });
      } else {
        setItems((prevItems) => {
          const newItems = prevItems.map((item) =>
            item.id === id
              ? {
                  ...item,
                  selectedField: newValue,
                  selectedHumanOperator: HumanOperator.Equal,
                  selectedValue: '',
                }
              : item
          );
          queueMicrotask(() => generateDefinition(newItems));
          return newItems;
        });
      }
    },
    [generateDefinition, filterOptions]
  );

  const handleOperatorChange = useCallback(
    (id: string, newValue: HumanOperator | undefined) => {
      setItems((prevItems) => {
        const newItems = prevItems.map((item) =>
          item.id === id ? { ...item, selectedHumanOperator: newValue } : item
        );
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition]
  );

  const handleValueChange = useCallback(
    (id: string, newValue: string | undefined) => {
      setItems((prevItems) => {
        const newItems = prevItems.map((item) =>
          item.id === id ? { ...item, selectedValue: newValue } : item
        );
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition]
  );

  const addItem = useCallback(() => {
    setItems((prevItems) => [
      ...prevItems,
      {
        id: uuidv4(),
        selectedLogicalOperator:
          prevItems.length > 1 && prevItems[1].selectedLogicalOperator
            ? prevItems[1].selectedLogicalOperator
            : 'and',
        selectedField: undefined,
        selectedHumanOperator: undefined,
        selectedValue: undefined,
      },
    ]);
  }, []);

  const removeItem = useCallback(
    (id: string) => {
      setItems((prevItems) => {
        const newItems = prevItems.filter((item) => item.id !== id);
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition]
  );

  return (
    <div id={id}>
      {items.map((item, index) => (
        <BuildItem
          key={item.id}
          isFirst={index === 0}
          item={item}
          onLogicalOperatorChange={handleLogicOperatorChange}
          onFieldChange={handleFieldChange}
          onOperatorChange={handleOperatorChange}
          onValueChange={handleValueChange}
          onRemoveItem={removeItem}
          onAddItem={addItem}
        />
      ))}
    </div>
  );
};
